import { useState, useCallback, useEffect, useRef } from 'react';
import {
  NginxDashboardSummary,
  NginxSiteDetail,
  NginxAdsComparison,
  NginxCollectionRun,
} from '../types';
import * as nginxAnalyticsService from '../services/nginx-analytics.service';

export interface DateRange {
  from: string;
  to: string;
}

interface UseNginxAnalyticsReturn {
  // State
  dashboard: NginxDashboardSummary | null;
  siteDetails: Map<number, NginxSiteDetail>;
  adsComparison: NginxAdsComparison | null;
  availableDates: string[];
  dateRange: DateRange | null;
  recentCollections: NginxCollectionRun[];
  isLoading: boolean;
  isCollecting: boolean;
  error: string | null;

  // Actions
  fetchDashboard: (dateFrom?: string, dateTo?: string) => Promise<void>;
  fetchSiteDetail: (siteId: number, dateFrom?: string, dateTo?: string) => Promise<void>;
  fetchAdsComparison: (dateFrom?: string, dateTo?: string) => Promise<void>;
  triggerCollection: () => Promise<void>;
  selectDateRange: (range: DateRange) => void;
  refreshData: () => Promise<void>;
}

export function useNginxAnalytics(accessToken: string | undefined): UseNginxAnalyticsReturn {
  const [dashboard, setDashboard] = useState<NginxDashboardSummary | null>(null);
  const [siteDetails, setSiteDetails] = useState<Map<number, NginxSiteDetail>>(new Map());
  const [adsComparison, setAdsComparison] = useState<NginxAdsComparison | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [recentCollections, setRecentCollections] = useState<NginxCollectionRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollecting, setIsCollecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchDashboard = useCallback(
    async (dateFrom?: string, dateTo?: string) => {
      if (!accessToken) return;

      setError(null);

      try {
        const data = await nginxAnalyticsService.getDashboard(accessToken, dateFrom, dateTo);
        setDashboard(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dashboard');
      }
    },
    [accessToken]
  );

  const fetchSiteDetail = useCallback(
    async (siteId: number, dateFrom?: string, dateTo?: string) => {
      if (!accessToken) return;

      try {
        const data = await nginxAnalyticsService.getSiteDetail(accessToken, siteId, dateFrom, dateTo);
        setSiteDetails((prev) => new Map(prev).set(siteId, data));
      } catch (err) {
        console.error('Error fetching site detail:', err);
      }
    },
    [accessToken]
  );

  const fetchAdsComparison = useCallback(
    async (dateFrom?: string, dateTo?: string) => {
      if (!accessToken) return;

      try {
        const data = await nginxAnalyticsService.getAdsComparison(accessToken, dateFrom, dateTo);
        setAdsComparison(data);
      } catch (err) {
        console.error('Error fetching ads comparison:', err);
      }
    },
    [accessToken]
  );

  const triggerCollection = useCallback(async () => {
    if (!accessToken) return;

    setIsCollecting(true);
    setError(null);

    try {
      await nginxAnalyticsService.triggerCollection(accessToken);
      // Fetch collections after a short delay
      timeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        const collections = await nginxAnalyticsService.getRecentCollections(accessToken, 5);
        if (mountedRef.current) setRecentCollections(collections);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar coleta');
    } finally {
      setIsCollecting(false);
    }
  }, [accessToken]);

  const selectDateRange = useCallback((range: DateRange) => {
    setDateRange(range);
  }, []);

  const refreshData = useCallback(async () => {
    if (!accessToken) return;

    await fetchDashboard(dateRange?.from, dateRange?.to);
    await fetchAdsComparison(dateRange?.from, dateRange?.to);
  }, [accessToken, dateRange, fetchDashboard, fetchAdsComparison]);

  // Cleanup on unmount: cancel pending timeout and stop stale state updates
  useEffect(() => () => {
    mountedRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (!accessToken || initializedRef.current) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch available dates first
        const dates = await nginxAnalyticsService.getAvailableDates(accessToken);
        setAvailableDates(dates);

        // Use most recent date or today
        const today = new Date().toISOString().split('T')[0];
        const defaultDate = dates.length > 0 ? dates[0] : today;
        setDateRange({ from: defaultDate, to: defaultDate });

        // Fetch dashboard and collections
        await Promise.all([
          fetchDashboard(defaultDate, defaultDate),
          fetchAdsComparison(defaultDate, defaultDate),
          nginxAnalyticsService.getRecentCollections(accessToken, 5).then(setRecentCollections),
        ]);

        initializedRef.current = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados iniciais');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [accessToken, fetchDashboard, fetchAdsComparison]);

  // Refetch data when date range changes (but skip if this is the initial load)
  useEffect(() => {
    if (dateRange && accessToken && initializedRef.current) {
      setIsLoading(true);
      Promise.all([
        fetchDashboard(dateRange.from, dateRange.to),
        fetchAdsComparison(dateRange.from, dateRange.to),
      ]).finally(() => setIsLoading(false));
      // Clear site details when date changes
      setSiteDetails(new Map());
    }
  }, [dateRange, accessToken, fetchDashboard, fetchAdsComparison]);

  return {
    dashboard,
    siteDetails,
    adsComparison,
    availableDates,
    dateRange,
    recentCollections,
    isLoading,
    isCollecting,
    error,
    fetchDashboard,
    fetchSiteDetail,
    fetchAdsComparison,
    triggerCollection,
    selectDateRange,
    refreshData,
  };
}
